// Persistent browser device id + light fingerprint, sent to the API for fraud
// prevention. The id is mirrored in localStorage, a cookie and IndexedDB so
// clearing one store restores it from another. Logout never clears it.

const KEY = 'ac_did';
const ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;
const IDB_NAME = 'ac_store';
const IDB_STORE = 'kv';

let deviceId = null;
let fingerprint = null;
let freshlyGenerated = false;
let initPromise = null;

const isValid = (id) => typeof id === 'string' && ID_PATTERN.test(id);

const readLocal = () => {
  try { return localStorage.getItem(KEY); } catch { return null; }
};

const writeLocal = (id) => {
  try { localStorage.setItem(KEY, id); } catch { /* storage blocked */ }
};

const readCookie = () => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${KEY}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch { return null; }
};

const writeCookie = (id) => {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${KEY}=${encodeURIComponent(id)}; Max-Age=31536000; Path=/; SameSite=Lax${secure}`;
  } catch { /* cookies blocked */ }
};

const withIdb = (mode, fn) => new Promise((resolve) => {
  try {
    const open = indexedDB.open(IDB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(IDB_STORE);
    open.onerror = () => resolve(null);
    open.onsuccess = () => {
      try {
        const tx = open.result.transaction(IDB_STORE, mode);
        const req = fn(tx.objectStore(IDB_STORE));
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => resolve(null);
      } catch { resolve(null); }
    };
  } catch { resolve(null); }
});

const readIdb = () => withIdb('readonly', (store) => store.get(KEY));
const writeIdb = (id) => withIdb('readwrite', (store) => store.put(id, KEY));

const generateId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
};

const persist = (id) => {
  writeLocal(id);
  writeCookie(id);
  writeIdb(id);
};

export const getDeviceId = () => {
  if (deviceId) return deviceId;
  const stored = [readLocal(), readCookie()].find(isValid);
  deviceId = stored || generateId();
  freshlyGenerated = !stored;
  writeLocal(deviceId);
  writeCookie(deviceId);
  // A fresh id must not overwrite one that survived in IndexedDB; initDevice() checks first.
  if (!freshlyGenerated) writeIdb(deviceId);
  return deviceId;
};

const canvasHash = () => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 220;
    canvas.height = 40;
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = '#f60';
    ctx.fillRect(100, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('ArabCap, عربي 🎬', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('ArabCap, عربي 🎬', 4, 17);
    return canvas.toDataURL();
  } catch { return ''; }
};

const computeFingerprint = async () => {
  if (!window.crypto?.subtle) return null;
  const nav = window.navigator || {};
  const parts = [
    nav.userAgent,
    (nav.languages || []).join(','),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    `${window.screen?.width}x${window.screen?.height}x${window.screen?.colorDepth}`,
    nav.hardwareConcurrency,
    nav.deviceMemory,
    nav.platform,
    canvasHash(),
  ].join('|');
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(parts));
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

// Restore an id that survived only in IndexedDB, then compute the fingerprint.
export const initDevice = () => {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    getDeviceId();
    if (freshlyGenerated) {
      const fromIdb = await Promise.race([readIdb(), new Promise((r) => setTimeout(() => r(null), 1000))]);
      if (isValid(fromIdb)) deviceId = fromIdb;
      persist(deviceId);
    }
    try { fingerprint = await computeFingerprint(); } catch { fingerprint = null; }
  })();
  return initPromise;
};

export const getDeviceHeaders = () => {
  const headers = { 'X-Device-Id': getDeviceId() };
  if (fingerprint) headers['X-Device-Fp'] = fingerprint;
  return headers;
};
